/**
 * @file apps/web/src/components/FDDEditor.tsx
 * @description Tiptap-based rich-text editor for FDD (Functional Design Document) sections.
 * Left sidebar lists sections; right panel shows the active section's Tiptap editor.
 * Auto-saves on change (debounced 800ms). Supports AI regeneration per section.
 * STUB: AI regeneration calls claudeStub via the /documents/:id/sections/:key/regenerate API.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, List, ListOrdered, Heading2, Heading3,
  Quote, Code, Minus, Undo, Redo, Sparkles, Save, Loader2,
} from 'lucide-react';
import { Badge } from './Badge.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FDDSection {
  id?:           string;
  section_key:   string;
  section_title: string;
  section_order: number;
  content_md:    string;
  ai_generated?: boolean;
  updated_at?:   string;
}

interface FDDEditorProps {
  docId:           string;
  sections:        FDDSection[];
  loading?:        boolean;
  readOnly?:       boolean;
  onSave:          (key: string, contentMd: string) => Promise<void>;
  onRegenerate?:   (key: string) => Promise<void>;
  className?:      string;
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

function ToolbarButton({
  onClick, active, disabled, title, children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      disabled={disabled}
      title={title}
      className={[
        'w-7 h-7 rounded flex items-center justify-center transition-colors text-sm',
        active
          ? 'bg-blue text-white'
          : 'text-ink/60 hover:bg-gray hover:text-ink',
        disabled ? 'opacity-30 cursor-not-allowed' : '',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor }: { editor: Editor | null }) {
  if (!editor) return null;

  return (
    <div className="flex items-center gap-0.5 px-3 py-1.5 border-b border-[rgba(8,17,31,0.07)] bg-gray/40 flex-wrap">
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo"
      >
        <Undo size={13} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo"
      >
        <Redo size={13} />
      </ToolbarButton>

      <div className="w-px h-4 bg-ink/10 mx-1" />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={editor.isActive('heading', { level: 2 })}
        title="Heading 2"
      >
        <Heading2 size={13} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        active={editor.isActive('heading', { level: 3 })}
        title="Heading 3"
      >
        <Heading3 size={13} />
      </ToolbarButton>

      <div className="w-px h-4 bg-ink/10 mx-1" />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
        title="Bold"
      >
        <Bold size={13} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
        title="Italic"
      >
        <Italic size={13} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        active={editor.isActive('code')}
        title="Inline code"
      >
        <Code size={13} />
      </ToolbarButton>

      <div className="w-px h-4 bg-ink/10 mx-1" />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')}
        title="Bullet list"
      >
        <List size={13} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')}
        title="Numbered list"
      >
        <ListOrdered size={13} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        active={editor.isActive('blockquote')}
        title="Blockquote"
      >
        <Quote size={13} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Divider"
      >
        <Minus size={13} />
      </ToolbarButton>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section sidebar item
// ---------------------------------------------------------------------------

function SectionItem({
  section,
  active,
  onClick,
}: {
  section: FDDSection;
  active:  boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        'w-full text-left px-3 py-2 rounded text-sm transition-colors flex items-start gap-2',
        active
          ? 'bg-sky text-blue font-medium'
          : 'text-ink/70 hover:bg-gray hover:text-ink',
      ].join(' ')}
    >
      <span className="font-mono text-[10px] text-muted/70 mt-0.5 shrink-0 w-5">
        {String(section.section_order).padStart(2, '0')}
      </span>
      <span className="leading-snug">{section.section_title}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// FDDEditor
// ---------------------------------------------------------------------------

export function FDDEditor({ sections, loading, readOnly = false, onSave, onRegenerate, className = '' }: FDDEditorProps) {
  const [activeKey, setActiveKey]     = useState<string>(sections[0]?.section_key ?? '');
  const [saving, setSaving]           = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);
  const saveTimerRef                  = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeSection = sections.find((s) => s.section_key === activeKey) ?? sections[0];

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: 'Start writing or click the AI button to regenerate this section…',
      }),
    ],
    content:      activeSection?.content_md ?? '',
    editable:     !readOnly,
    editorProps: {
      attributes: { class: 'tiptap-editor ProseMirror' },
    },
    onUpdate: ({ editor: e }) => {
      // Auto-save debounced
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        if (!activeSection) return;
        setSaving(true);
        try {
          await onSave(activeSection.section_key, e.getText() ? e.getHTML() : '');
        } finally {
          setSaving(false);
        }
      }, 800);
    },
  });

  // Sync editor content when section changes
  useEffect(() => {
    if (!editor || !activeSection) return;
    if (editor.getHTML() !== activeSection.content_md) {
      editor.commands.setContent(activeSection.content_md ?? '');
    }
  }, [activeKey, activeSection, editor]);

  // Reactively lock/unlock the editor. useEditor's `editable` option only
  // applies on initial mount — Tiptap's internal re-render path re-applies
  // the editor's CURRENT editable state on every subsequent render (see
  // @tiptap/react's EditorInstanceManager.onRender), so changing `readOnly`
  // alone does nothing after mount. setEditable() is the documented,
  // imperative way to make this reactive.
  useEffect(() => {
    if (editor) editor.setEditable(!readOnly, false);
  }, [editor, readOnly]);

  // Ensure first section is selected when sections load
  useEffect(() => {
    if (sections.length > 0 && !activeKey) {
      setActiveKey(sections[0]!.section_key);
    }
  }, [sections, activeKey]);

  const handleRegenerate = useCallback(async () => {
    if (!activeSection || !onRegenerate) return;
    setRegenerating(true);
    try {
      await onRegenerate(activeSection.section_key);
    } finally {
      setRegenerating(false);
    }
  }, [activeSection, onRegenerate]);

  if (loading) {
    return (
      <div className={['card h-96 animate-pulse flex items-center justify-center', className].join(' ')}>
        <Loader2 size={24} className="text-muted animate-spin" />
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className={['card empty-state', className].join(' ')}>
        <p className="text-sm text-muted">No sections found. Generate a document first.</p>
      </div>
    );
  }

  return (
    <>
    <div className={['card flex overflow-hidden', className].join(' ')} style={{ minHeight: '520px' }}>
      {/* Section sidebar */}
      <aside
        className="w-48 shrink-0 border-r border-[rgba(8,17,31,0.07)] bg-gray/30 p-2 overflow-y-auto"
      >
        <p className="section-label px-2 mb-2">Sections</p>
        <nav className="space-y-0.5">
          {[...sections]
            .sort((a, b) => a.section_order - b.section_order)
            .map((s) => (
              <SectionItem
                key={s.section_key}
                section={s}
                active={s.section_key === activeKey}
                onClick={() => setActiveKey(s.section_key)}
              />
            ))
          }
        </nav>
      </aside>

      {/* Editor pane */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Section header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[rgba(8,17,31,0.07)]">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="font-display text-base font-semibold text-ink truncate">
              {activeSection?.section_title ?? 'Select a section'}
            </h3>
            {activeSection?.ai_generated && (
              <Badge variant="info" label="AI" size="xs" />
            )}
          </div>
          <div className="flex items-center gap-2">
            {saving && (
              <span className="flex items-center gap-1 text-[11px] font-mono text-muted">
                <Loader2 size={10} className="animate-spin" /> Saving…
              </span>
            )}
            {onRegenerate && !readOnly && (
              <button
                onClick={() => setConfirmingRegenerate(true)}
                disabled={regenerating}
                className="btn-secondary py-1 px-3 text-xs"
                title="Regenerate this section with AI"
              >
                {regenerating
                  ? <Loader2 size={12} className="animate-spin" />
                  : <Sparkles size={12} />
                }
                {regenerating ? 'Regenerating…' : 'Regenerate'}
              </button>
            )}
          </div>
        </div>

        {/* Toolbar */}
        <Toolbar editor={editor} />

        {/* Tiptap content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 tiptap-editor">
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>

    {confirmingRegenerate && activeSection && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
        <div className="card p-5 max-w-sm w-full">
          <h3 className="font-display text-base font-semibold text-ink mb-2">Regenerate this section?</h3>
          <p className="text-sm text-muted mb-4">
            This will overwrite the current content of &ldquo;{activeSection.section_title}&rdquo; with new
            AI-generated content. This cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setConfirmingRegenerate(false)} className="btn-ghost text-sm">
              Cancel
            </button>
            <button
              onClick={() => { setConfirmingRegenerate(false); void handleRegenerate(); }}
              className="btn-primary text-sm"
            >
              Regenerate
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
